import type { MerchantHomeOpportunity, MerchantHomeOverview, MerchantHomeWorkItem } from "./merchantHomeSchemas";
import type { MerchantHomeApproval, MerchantHomeOpportunity as OpportunityView, MerchantHomeViewProps } from "./ui/merchantHomeModels";
import { isMonitorCard, monitorActivity, monitorCardSummary, monitorStateLabel } from "./merchantHomeMonitorPresentation";
import type { WorkbenchCardType, WorkbenchContent, WorkbenchSourceStatus } from "./workbench/contentSchemas";
import { isStoreManagementSection } from "./workbench/storeManagementSchemas";
import { presentStoreManagementSection, storeManagementSectionLabels } from "./workbench/storeManagementPresentation";

const labels: Record<WorkbenchCardType, string> = {
  daily_product: "日批商品机会", advertising_content: "广告内容机会", auto_listing: "自动上品", promotion_calendar: "经营日历",
  media_reference: "媒体参考", competitor_updates: "竞品动态", creator_tracking: "达人追踪", market_trends: "市场趋势",
  monthly_opportunity: "月度商机", store_opening: "店铺开通", pricing_calculator: "定价计算器", product_selection_analysis: "选品分析",
  creator_recommendation: "达人推荐", report_export: "报告导出", content_strategy: "内容策略", policy_compliance: "政策合规",
  store_operations: "店铺经营", product_performance: "商品表现", product_diagnosis: "商品诊断", advertising_management: "广告管理",
  advertising_performance: "广告效果", inventory_management: "库存管理", fulfillment_logistics: "履约物流", after_sales_reviews: "售后评价",
  profit_settlement: "利润结算", social_media: "社媒管理", email_management: "邮件管理",
};
export const workStatusLabels = { succeeded: "已完成", running: "运行中", approval_required: "待审批", waiting_input: "等待补充信息", blocked: "受阻", failed: "执行失败", expired: "已过期" };
export function homeDate(value: string | null | undefined, timezone = "Asia/Shanghai") {
  if (!value) return "";
  // 全天日期是来源的日历日期，不按店铺时区换算成前一天。
  if (/^\d{4}-\d{2}-\d{2}$/u.test(value)) return value.replaceAll("-", "/");
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  try { return new Intl.DateTimeFormat("zh-CN", { timeZone: timezone, year: "numeric", month: "2-digit", day: "2-digit" }).format(date); } catch { return value; }
}
export function homeGreeting(timezone: string, at = new Date()) {
  try {
    const hour = Number(new Intl.DateTimeFormat("en-GB", { timeZone: timezone, hour: "2-digit", hourCycle: "h23" }).format(at));
    return hour < 6 ? "夜深了" : hour < 12 ? "上午好" : hour < 18 ? "下午好" : "晚上好";
  } catch { return "你好"; }
}
export function safeMediaURL(value: string) {
  try { const url = new URL(value); return url.protocol === "https:" && !url.username && !url.password ? url.href : undefined; } catch { return undefined; }
}

const sectionLabels: Record<string, string> = {
  rationale: "机会依据",
  audience: "适合人群",
  suggestions: "建议",
  copy: "广告文案",
  evidence: "趋势依据",
  reference_notes: "借鉴说明",
  storyboard: "分镜",
  draft_details: "草稿说明",
  events: "时间信息",
  exchange_rate: "汇率参考",
  objects: "对象列表",
  changes: "变化摘要",
  tracking_status: "状态摘要",
  metrics: "趋势指标",
  structure: "机会结构",
  coverage: "覆盖范围",
  progress: "开店进度",
  steps: "步骤",
  material_gaps: "材料缺口",
  assumptions: "计算假设",
  breakdown: "费率拆分",
  result: "建议价格",
  candidates: "候选商品",
  reasons: "分析理由",
  creators: "达人候选",
  match: "匹配依据",
  snapshot: "报告快照",
  status: "生成状态",
  resource: "云盘资源",
  dimensions: "策略维度",
  source_state: "来源状态",
  rules: "规则",
};

const mediaRelations = {
  EXACT_PRODUCT: "同商品参考",
  EXACT_CATEGORY: "同品类参考",
  OPTIONAL_DIRECT_CONTEXT: "相关场景参考",
  ANALOG_ONLY: "类比参考",
} as const;

const freshnessLabels: Record<"fresh" | "stale", string> = { fresh: "资料新鲜", stale: "历史资料" };
const coverageLabels: Record<WorkbenchSourceStatus["coverage"]["state"], string> = { complete: "完整覆盖", partial: "部分覆盖", unknown: "覆盖量未由来源证实" };
const syncFailureLabels = {
  provider_authorization_failed: "平台授权失败",
  provider_contract_or_data_invalid: "平台数据格式异常",
  provider_unavailable: "平台暂不可用",
  source_sync_failed: "同步失败",
} as const;
const schedulerLabels = {
  enabled: "自动同步已开启",
  disabled: "自动同步已关闭",
  not_configured: "自动同步未配置",
  unsupported: "当前平台不支持自动同步",
} as const;
const latestRunLabels = {
  none: "尚无同步任务",
  running: "正在同步",
  retry_wait: "最近同步等待重试",
  succeeded: "最近同步任务已完成",
  partial: "最近同步仅部分完成",
  canceled: "最近同步已取消",
} as const;
const openingStatusLabels = { not_started: "尚未开始", in_progress: "进行中", needs_documents: "待补材料", blocked: "受阻", completed: "已完成" } as const;
const openingStepLabels = { pending: "待处理", in_progress: "进行中", completed: "已完成", blocked: "受阻" } as const;
const strategyLabels = { channel: "渠道", audience: "受众", budget: "预算", product: "商品", content: "内容", promotion: "促销" } as const;
const policyStateLabels = { compliant: "符合", attention: "需关注", unknown: "待确认" } as const;
const bpsPercent = (value: number) => String(value / 100);

export function workbenchSourceStatusView(status: WorkbenchSourceStatus | null, timezone: string) {
  if (!status) return undefined;
  const freshnessLabel = status.freshness.state === "unknown"
    ? status.freshness.basis === "source_time_unavailable" ? "来源未提供采集时间" : "尚未评估来源时间"
    : freshnessLabels[status.freshness.state];
  const coverageLabel = status.coverage.state === "unknown"
    ? status.coverage.reason_code === "provider_total_unknown" ? "来源未给出完整总量"
      : status.coverage.reason_code === "source_unavailable" ? "来源暂不可用"
        : status.coverage.reason_code === "source_read_failed" ? "来源读取失败" : "尚未证实覆盖范围"
    : coverageLabels[status.coverage.state];
  const syncHealth = "snapshot_schema" in status ? status.sync_health : undefined;
  const syncHealthLabel = syncHealth ? [
    schedulerLabels[syncHealth.scheduler_state],
    syncHealth.latest_run_state === "failed"
      ? `最近同步失败${syncHealth.failure_reason ? `：${syncFailureLabels[syncHealth.failure_reason]}` : ""}`
      : latestRunLabels[syncHealth.latest_run_state],
  ].filter(Boolean).join(" · ") : "";
  return {
    asOf: homeDate(status.as_of, timezone) || undefined,
    freshnessLabel,
    coverageLabel,
    syncHealthLabel: syncHealthLabel || undefined,
  };
}

// `/card-content` 是正式大小卡唯一来源；这里是唯一 canonical DTO -> 小卡 ViewModel 投影。
export function workbenchOpportunityView(card: WorkbenchContent, timezone: string, market = ""): OpportunityView {
  const sourceStatus = workbenchSourceStatusView(card.source_status, timezone);
  const objects = card.sections.find(section => section.kind === "objects" && section.state === "available");
  const objectCount = objects?.kind === "objects" && objects.data ? objects.data.object_count : undefined;
  const summary = monitorCardSummary(card.card_type, card.state, card.summary, objectCount);
  const facts: { label: string; text: string }[] = [];
  const reasons: { label: string; text: string }[] = [];
  const rows: { id: string; title: string; description?: string; label?: string }[] = [];
  const metrics: NonNullable<OpportunityView["metrics"]>[number][] = [];

  for (const section of card.sections) {
    if (section.state !== "available" || !section.data) continue;
    if (isStoreManagementSection(section)) {
      const view = presentStoreManagementSection(section, value => homeDate(value, timezone));
      facts.push(...view.facts);
      metrics.push(...view.metrics);
      rows.push(...view.rows);
      continue;
    }
    switch (section.kind) {
      case "text": {
        const target = section.key === "suggestions" ? reasons : facts;
        const label = sectionLabels[section.key] ?? section.key;
        section.data.paragraphs.forEach((text, index) => target.push({ label: `${label}${section.data!.paragraphs.length > 1 ? ` ${index + 1}` : ""}`, text }));
        break;
      }
      case "storyboard":
        section.data.frames.forEach(frame => rows.push({ id: `${section.key}:${frame.index}`, title: `镜头 ${frame.index}`, description: frame.visual, label: frame.dialogue || undefined }));
        break;
      case "draft":
        rows.push({ id: section.data.draft_id, title: "商品草稿", description: section.data.requires_confirmation ? "需要确认后才能继续发布" : section.data.status, label: section.data.provider || undefined });
        break;
      case "events":
        section.data.events.forEach(event => rows.push({ id: event.id, title: event.title, description: [homeDate(event.date, event.timezone || timezone), event.region, event.note].filter(Boolean).join(" · "), label: event.timezone || section.data!.timezone }));
        break;
      case "exchange_rate":
        metrics.push({ id: `${section.key}:${section.data.base_currency}`, label: `${section.data.base_currency}/${section.data.quote_currency}`, value: String(section.data.rate), unit: section.data.quote_currency, comparison: `${homeDate(section.data.date, timezone)} · 仅供参考` });
        break;
      case "objects":
        section.data.objects.forEach(item => rows.push({ id: item.id, title: item.label, description: monitorStateLabel(item.state) }));
        break;
      case "changes":
        if ("observed_event_count" in section.data) facts.push({ label: "变化覆盖", text: section.data.truncated ? `显示最近 ${section.data.changes.length} 条，本地已读取 ${section.data.observed_event_count} 条` : `本地已读取 ${section.data.observed_event_count} 条，已全部显示` });
        if (!section.data.changes.length) facts.push({ label: "变化摘要", text: "近24小时暂无已读取变化" });
        else section.data.changes.forEach(change => rows.push({ id: change.id, title: change.summary, description: homeDate(change.observed_at, timezone) }));
        break;
      case "tracking_status": {
        const activity = monitorActivity(section.data.observed_event_count);
        facts.push({ label: "观察范围", text: section.data.complete ? "完整" : "部分观察" });
        facts.push({ label: activity.label, text: activity.value });
        break;
      }
      case "metrics":
        section.data.metrics.forEach((metric, index) => metrics.push({ id: `${section.key}:${index}`, label: metric.label, value: String(metric.value), unit: metric.unit || undefined, comparison: metric.comparison_period || undefined }));
        break;
      case "monthly_structure":
        facts.push({ label: "机会月份", text: section.data.month });
        section.data.opportunities.forEach(item => rows.push({ id: item.id, title: item.title, description: item.summary }));
        break;
      case "evidence":
        section.data.items.forEach(item => facts.push({ label: item.label, text: item.summary }));
        break;
      case "coverage":
        facts.push({ label: sectionLabels[section.key] ?? "覆盖范围", text: coverageLabels[section.data.state] });
        break;
      case "opening_progress":
        facts.push({ label: "开店进度", text: openingStatusLabels[section.data.status] });
        facts.push({ label: "平台与市场", text: `${section.data.provider} · ${section.data.market}` });
        facts.push({ label: "进度版本", text: section.data.revision });
        break;
      case "opening_steps":
        section.data.items.forEach(item => rows.push({ id: item.key, title: item.label, description: openingStepLabels[item.state] }));
        break;
      case "material_gaps":
        section.data.items.forEach(item => rows.push({ id: `gap:${item.code}`, title: item.label, description: item.required ? "必须补充" : "建议补充" }));
        break;
      case "pricing_assumptions":
        metrics.push({ id: "product_cost_minor", label: "商品成本", value: section.data.product_cost_minor, unit: `${section.data.currency} 最小货币单位`, comparison: section.data.formula_version });
        metrics.push({ id: "logistics_minor", label: "物流成本", value: section.data.logistics_minor, unit: `${section.data.currency} 最小货币单位` });
        break;
      case "pricing_breakdown":
        metrics.push({ id: "tax_bps", label: "税费", value: bpsPercent(section.data.tax_bps), unit: "%" });
        metrics.push({ id: "commission_bps", label: "佣金", value: bpsPercent(section.data.commission_bps), unit: "%" });
        metrics.push({ id: "target_margin_bps", label: "目标利润", value: bpsPercent(section.data.target_margin_bps), unit: "%" });
        break;
      case "pricing_result":
        metrics.push({ id: section.data.preview_id, label: "建议售价", value: section.data.recommended_price_minor, unit: "最小货币单位", comparison: `计算版本 ${section.data.revision}` });
        break;
      case "selection_candidates":
        section.data.items.forEach(item => rows.push({ id: item.id, title: item.label, description: item.rationale, label: `匹配度 ${bpsPercent(item.score_bps)}%` }));
        break;
      case "creator_candidates":
        section.data.items.forEach(item => rows.push({ id: item.id, title: item.display_label, description: item.rationale, label: `匹配度 ${bpsPercent(item.match_bps)}%` }));
        break;
      case "report_snapshot":
        facts.push({ label: "报告快照", text: `版本 ${section.data.snapshot_version} · ${homeDate(section.data.snapshot_as_of, timezone)}` });
        break;
      case "report_status":
        facts.push({ label: "生成状态", text: "报告已在组织云盘中可用" });
        break;
      case "report_resource":
        rows.push({ id: section.data.resource_id, title: section.data.display_name, description: section.data.mime_type });
        break;
      case "strategy_dimensions":
        section.data.items.forEach(item => rows.push({ id: item.key, title: `${strategyLabels[item.key]} · ${item.headline}`, description: item.summary || undefined }));
        break;
      case "strategy_source_state":
        facts.push({ label: "策略覆盖", text: section.data.missing_dimensions.length ? `待补：${section.data.missing_dimensions.map(key => strategyLabels[key]).join("、")}` : "六个维度均已确认" });
        break;
      case "policy_rules":
        section.data.items.forEach(item => rows.push({ id: item.id, title: item.label, description: policyStateLabels[item.state] }));
        break;
      case "policy_events":
        if (!section.data.items.length) facts.push({ label: "政策事件", text: "暂无已确认事件" });
        else section.data.items.forEach(item => rows.push({ id: item.id, title: item.summary, description: homeDate(item.observed_at, timezone) }));
        break;
      case "policy_status":
        facts.push({ label: "读取状态", text: "仅展示已确认的管理端资料，自动政策推送尚未闭环" });
        break;
    }
  }

  const mediaURL = card.media ? safeMediaURL(card.media.public_url) : undefined;
  return {
    id: card.content_id,
    type: card.card_type,
    state: card.state,
    errorCode: card.error_code,
    category: labels[card.card_type],
    title: card.title,
    description: card.state === "ready" ? summary : undefined,
    stateMessage: summary,
    market: market || undefined,
    dateLabel: sourceStatus?.asOf,
    badge: sourceStatus?.freshnessLabel,
    confidenceLabel: sourceStatus?.coverageLabel,
    syncHealthLabel: sourceStatus?.syncHealthLabel,
    facts: facts.length ? facts : undefined,
    reasons: reasons.length ? reasons : undefined,
    rows: rows.length ? rows : undefined,
    metrics: metrics.length ? metrics : undefined,
    media: card.media && mediaURL ? { src: mediaURL, kind: card.media.media_type, alt: card.title, relation: mediaRelations[card.media.relation] } : undefined,
    sectionStates: card.sections.map(section => ({ key: section.key, label: isStoreManagementSection(section) ? storeManagementSectionLabels[section.kind] : sectionLabels[section.key] ?? section.key, kind: section.kind, state: section.state, missingReason: section.missing_reason || undefined })),
    sourceLabel: [...new Set(card.source_refs.map(source => source.label).filter(Boolean))].join(" · "),
    sourceAsOf: sourceStatus?.asOf,
    actions: card.actions.map((action, index) => ({ id: String(index), label: action.label, disabledReason: !action.enabled ? action.disabled_reason || "当前条件暂不支持此操作" : undefined })),
  };
}

export function opportunityView(card: MerchantHomeOpportunity, timezone: string): OpportunityView {
  const payload = card.payload;
  const mediaURL = "public_url" in payload ? safeMediaURL(payload.public_url) : undefined;
  const summary = monitorCardSummary(card.type, card.state, payload.summary, "object_count" in payload ? payload.object_count : undefined);
  const activity = isMonitorCard(card.type) && card.state === "ready" && "update_count" in payload ? monitorActivity(payload.update_count) : undefined;
  return {
    id: card.id, type: card.type, state: card.state, category: labels[card.type], title: card.title,
    description: card.state === "ready" ? summary : undefined, stateMessage: summary, market: card.scope.market || undefined,
    dateLabel: homeDate(card.published_at ?? "", timezone),
    badge: "stale" in payload && payload.stale ? "历史资料" : undefined,
    facts: activity ? [{ label: activity.label, text: activity.value }] : "facts" in payload ? payload.facts.map((text, index) => ({ label: `资料 ${index + 1}`, text })) : undefined,
    reasons: "suggestions" in payload ? payload.suggestions.map((text, index) => ({ label: `建议 ${index + 1}`, text })) : undefined,
    rows: "objects" in payload ? payload.objects.map(item => ({ id: item.id, title: item.label, description: monitorStateLabel(item.state) })) : "draft_id" in payload ? [{ id: payload.draft_id, title: "商品草稿", description: payload.requires_confirmation ? "需要确认后才能继续发布" : payload.status }] : undefined,
    media: mediaURL && "media_type" in payload && ["image", "video"].includes(payload.media_type) ? { src: mediaURL, kind: payload.media_type as "image" | "video", alt: card.title, relation: ({ same_product: "同品参考", analogy: "类比参考", analogous: "类比参考" } as Record<string, string>)[payload.relation] ?? payload.relation } : undefined,
    sourceLabel: [...new Set(card.source_refs.map(source => source.label).filter(Boolean))].join(" · "),
    sourceAsOf: card.source_refs.length ? homeDate(card.source_refs.find(source => source.as_of)?.as_of || card.updated_at, timezone) : undefined,
    actions: card.actions.map((action, index) => ({ id: String(index), label: action.label, disabledReason: !action.enabled ? action.disabled_reason || "当前条件暂不支持此操作" : undefined })),
  };
}
export function approvalView(item: MerchantHomeWorkItem, status?: Pick<MerchantHomeApproval, "status" | "error">): MerchantHomeApproval {
  const gmailConfirmation = item.actions.disabled_reason === "gmail_conversation_approval_required";
  return { id: item.item_id, category: "待审批事项", title: item.safe_title, description: item.safe_summary, metrics: [], judgmentLabel: "确认执行范围", judgment: "批准后，Merry 会按这份方案在当前店铺执行。如需修改，请先调整。", status: status?.status ?? "pending", error: status?.error,
    approveDisabledReason: gmailConfirmation ? "请返回原对话查看邮件预览，再确认发送。" : !item.actions.can_act ? item.actions.disabled_reason || "当前没有执行权限" : undefined,
    adjustLabel: gmailConfirmation ? "返回对话确认" : undefined,
    adjustDisabledReason: !item.source.available || item.source.stale ? "方案来源暂不可用，请重新读取" : undefined };
}
export function homeStrategy(overview?: MerchantHomeOverview): MerchantHomeViewProps["strategy"] {
  if (!overview) return { state: "loading" };
  const strategy = overview.strategy;
  if (strategy.state === "ready") return { state: "ready", summary: strategy.summary || strategy.dimensions.map(item => item.headline).join("；") };
  if (strategy.state === "empty") return { state: "empty" };
  return { state: "error", error: strategy.state === "forbidden" ? "当前没有读取经营策略的权限" : strategy.state === "requires_selection" ? "请先选择店铺" : "经营策略暂时无法读取，点击重试" };
}
